import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import Logout from '@/common/components/pages/Logout/Logout';
import {
  ITEM_TYPES,
  PAGE_PRODUCTS_CATEGORY,
  PAGE_PRODUCTS_DISCOUNT,
  PAGE_PRODUCTS_OFFERS,
  PAGE_PRODUCTS_SEARCH,
  PAGE_SECTION,
  PRODUCTS_API_NODE_ROOT_DISCOUNT,
  ROUTE_404,
  ROUTE_ACCOUNT,
  ROUTE_APP_CRAFTER,
  ROUTE_AUTO_LOGIN,
  ROUTE_CARTS,
  ROUTE_CHECKOUT,
  ROUTE_CHECKOUT_CONFIRM,
  ROUTE_CHOOSE_PASSWORD,
  ROUTE_CONTACT,
  ROUTE_ECATALOGS,
  ROUTE_EDIT,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_HOME,
  ROUTE_LEGALS,
  ROUTE_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_NODES,
  ROUTE_NODES_CATEGORY,
  ROUTE_ORDERS,
  ROUTE_PARTNERS,
  ROUTE_PRODUCT,
  ROUTE_QUOTES,
  ROUTE_REGISTER,
  ROUTE_RENEW_PASSWORD,
  ROUTE_SCAN_SELF_SERVICE,
  ROUTE_SCANBAT,
  ROUTE_SCANBAT_FORGOT_PASSWORD,
  ROUTE_SCANBAT_LOGIN,
  ROUTE_SEARCH,
  ROUTE_STANDALONE,
  ROUTE_STANDALONE_CART,
  ROUTE_STANDALONE_LOGIN,
  ROUTE_SUPPLIER,
  ROUTE_V2_TEST,
  ROUTE_VALIDATE,
  ROUTE_WISHLISTS,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useConfig } from '@/common/contexts/ConfigContext';
import lazyWithCatch from '@/common/helpers/lazyWithCatch';
import useMaintenanceSubscription from '@/common/hooks/mercure/useMaintenanceSubscription';
import PageLoader from '@/ebat/components/blocks/PageLoader/PageLoader';
import OrderLayout from '@/ebat/components/layouts/OrderLayout/OrderLayout';
import OrdersLayout from '@/ebat/components/layouts/OrdersLayout/OrdersLayout';
import ProductsLayout from '@/ebat/components/layouts/ProductsLayout/ProductsLayout';
import ScanbatLayout from '@/ebat/components/layouts/ScanbatLayout/ScanbatLayout';
import AppCrafter from '@/ebat/components/pages/AppCrafter/AppCrafter.router';
import AutoLogin from '@/ebat/components/pages/AutoLogin/AutoLogin';
import Home from '@/ebat/components/pages/Home/Home';
import Login from '@/ebat/components/pages/Login/Login';
import ScanbatLogin from '@/ebat/components/pages/Scanbat/Login/Login.container';
import Scanbat from '@/ebat/components/pages/Scanbat/Scanbat.router';
import ScanSelfService from '@/ebat/components/pages/ScanSelfService/ScanSelfService.container';
import StandaloneLogin from '@/ebat/components/pages/Standalone/Login/Login';
import Standalone from '@/ebat/components/pages/Standalone/Standalone';
import V2TestContainer from '@/ebat/components/pages/V2Test/V2Test.container';

import LegalNotice from '../components/blocks/Modal/Common/LegalNotice/LegalNotice';
import EcatalogLayout from '../components/layouts/EcatalogLayout/EcatalogLayout';
import QuoteLayout from '../components/layouts/QuoteLayout/QuoteLayout';
import QuotesLayout from '../components/layouts/QuotesLayout/QuotesLayout';
import SectionLayout from '../components/layouts/SectionLayout/SectionLayout';
import Sections from '../components/pages/Sections/Sections';
import PrivateRoute from './PrivateRoute';

const Wishlists = lazyWithCatch(() =>
  import('@/ebat/components/pages/Wishlists/Wishlists.container'),
);
const Register = lazyWithCatch(() =>
  import('@/ebat/components/pages/Register/Register'),
);
const RenewPassword = lazyWithCatch(() =>
  import('@/ebat/components/pages/RenewPassword/RenewPassword'),
);
const ChoosePassword = lazyWithCatch(() =>
  import('@/ebat/components/pages/ChoosePassword/ChoosePassword'),
);
const Products = lazyWithCatch(() =>
  import('@/ebat/components/pages/Products/Products.container'),
);
const Product = lazyWithCatch(() =>
  import('@/ebat/components/pages/Product/Product.container'),
);
const OffersCategory = lazyWithCatch(() =>
  import('@/ebat/components/pages/OffersCategory/OffersCategory.container'),
);
const DiscountCategory = lazyWithCatch(() =>
  import('@/ebat/components/pages/DiscountCategory/DiscountCategory.container'),
);
const Search = lazyWithCatch(() =>
  import('@/ebat/components/pages/Search/Search.container'),
);
const Cart = lazyWithCatch(() =>
  import('@/ebat/components/pages/Cart/Cart.container'),
);
const Account = lazyWithCatch(() =>
  import('@/ebat/components/pages/Account/Account'),
);
const Orders = lazyWithCatch(() =>
  import('@/ebat/components/pages/Orders/Orders.container'),
);
const Quotes = lazyWithCatch(() =>
  import('@/ebat/components/pages/Quotes/Quotes.container'),
);
const Order = lazyWithCatch(() =>
  import('@/ebat/components/pages/Order/Order.container'),
);

const Quote = lazyWithCatch(() =>
  import('@/ebat/components/pages/Quote/Quote.container'),
);
const Wishlist = lazyWithCatch(() =>
  import('@/ebat/components/pages/Wishlist/Wishlist'),
);
const Checkout = lazyWithCatch(() =>
  import('@/ebat/components/pages/Checkout/Checkout.container'),
);
const CheckoutConfirm = lazyWithCatch(() =>
  import('@/ebat/components/pages/CheckoutConfirm/CheckoutConfirm'),
);
const Contact = lazyWithCatch(() =>
  import('@/ebat/components/pages/Contact/Contact'),
);
const ECatalogs = lazyWithCatch(() =>
  import('@/ebat/components/pages/Cms/ECatalogs/ECatalogs'),
);
const ECatalog = lazyWithCatch(() =>
  import('@/ebat/components/pages/Cms/ECatalog/ECatalog'),
);
const Partners = lazyWithCatch(() =>
  import('@/ebat/components/pages/Cms/Partners/Partners'),
);
const Error404 = lazyWithCatch(() =>
  import('@/ebat/components/pages/Error404/Error404'),
);
const SupplierLogin = lazyWithCatch(() =>
  import('@/ebat/components/pages/Supplier/Login/Login.container'),
);
const Supplier = lazyWithCatch(() =>
  import('@/ebat/components/pages/Supplier/Supplier.container'),
);
// import Mirage from '../components/pages/Mirage/Mirage';
// TODO: Désactivé pour l'instant (demande du client)
// const IncompleteProfile = lazy(() =>
//   import(
//     '@/components/blocks/Modal/Common/IncompleteProfile/IncompleteProfile'
//   ),
// );

const Routes = () => {
  const { isEbat } = useConfig();
  const { isLoggedIn, user } = useAuth();
  useMaintenanceSubscription();

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {isEbat ? (
          <Route path={ROUTE_REGISTER} exact>
            {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <Register />}
          </Route>
        ) : null}
        <Route path={ROUTE_V2_TEST} exact>
          <V2TestContainer />
        </Route>
        <Route path={ROUTE_LOGIN} exact>
          {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <Login />}
        </Route>
        <Route path={ROUTE_AUTO_LOGIN} exact>
          <AutoLogin />
        </Route>
        <Route path={ROUTE_SCANBAT_LOGIN} exact>
          {isLoggedIn ? <Redirect to={ROUTE_SCANBAT} /> : <ScanbatLogin />}
        </Route>
        <Route path={ROUTE_SCANBAT_FORGOT_PASSWORD} exact>
          {isLoggedIn ? (
            <Redirect to={ROUTE_SCANBAT} />
          ) : (
            <ScanbatLogin paramIsOpen />
          )}
        </Route>
        <Route
          path={[
            `${ROUTE_SCAN_SELF_SERVICE}/:uid`,
            `${ROUTE_SCAN_SELF_SERVICE}/:uid/:codeOrRef*`,
          ]}
          exact
        >
          <ScanSelfService />
        </Route>
        <Route path={ROUTE_LEGALS} exact>
          <LegalNotice />
        </Route>
        <Route path={ROUTE_STANDALONE_LOGIN} exact>
          {isLoggedIn ? (
            <Redirect to={ROUTE_STANDALONE_CART} />
          ) : (
            <StandaloneLogin />
          )}
        </Route>
        <Route path={`${ROUTE_SUPPLIER}/:slug/login`} exact>
          {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <SupplierLogin />}
        </Route>
        <Route path={ROUTE_LOGOUT} exact>
          <Logout />
        </Route>
        <Route path={ROUTE_FORGOT_PASSWORD} exact>
          {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <Login paramIsOpen />}
        </Route>
        <Route path={ROUTE_RENEW_PASSWORD} exact>
          {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <RenewPassword />}
        </Route>
        <Route path={ROUTE_CHOOSE_PASSWORD} exact>
          {isLoggedIn ? <Redirect to={ROUTE_HOME} /> : <ChoosePassword />}
        </Route>
        <Route path={ROUTE_APP_CRAFTER}>
          <AppCrafter />
        </Route>
        <PrivateRoute path={ROUTE_HOME} exact>
          <Home />
        </PrivateRoute>

        <PrivateRoute
          layout={{
            Component: SectionLayout,
            props: {
              pageName: PAGE_SECTION,
            },
          }}
          path={[
            `${ROUTE_NODES_CATEGORY}/:depth1`,
            `${ROUTE_NODES_CATEGORY}/:depth1/:depth2`,
          ]}
          exact
        >
          <Sections />
        </PrivateRoute>

        <PrivateRoute
          layout={{
            Component: ProductsLayout,
            props: {
              pageName: PAGE_PRODUCTS_CATEGORY,
            },
          }}
          path={[
            `${ROUTE_NODES_CATEGORY}/:depth1/:depth2/:depth3`,
            `${ROUTE_NODES_CATEGORY}/:depth1/:depth2/:depth3/:depth4`,
          ]}
          exact
        >
          <Products />
        </PrivateRoute>
        <PrivateRoute
          layout={{
            Component: ProductsLayout,
            props: {
              pageName: PAGE_PRODUCTS_SEARCH,
            },
          }}
          path={ROUTE_SEARCH}
          exact
        >
          <Search />
        </PrivateRoute>
        <PrivateRoute
          layout={{
            Component: ProductsLayout,
            props: {
              pageName: PAGE_PRODUCTS_OFFERS,
            },
          }}
          path={`${ROUTE_NODES}/:category`}
          exact
        >
          <OffersCategory pageName={PAGE_PRODUCTS_OFFERS} />
        </PrivateRoute>
        <PrivateRoute
          layout={{
            Component: ProductsLayout,
            props: {
              pageName: PAGE_PRODUCTS_OFFERS,
            },
          }}
          path={`${ROUTE_NODES}/dynamic`}
          exact
        >
          <OffersCategory pageName={PAGE_PRODUCTS_OFFERS} />
        </PrivateRoute>

        <PrivateRoute
          layout={{
            Component: ProductsLayout,
            props: {
              pageName: PAGE_PRODUCTS_DISCOUNT,
            },
          }}
          path={`${ROUTE_NODES}/${PRODUCTS_API_NODE_ROOT_DISCOUNT}/:id`}
          exact
        >
          <DiscountCategory />
        </PrivateRoute>
        <PrivateRoute path={`${ROUTE_PRODUCT}/:path/:slug`} exact>
          <Product />
        </PrivateRoute>
        <PrivateRoute path={`${ROUTE_PRODUCT}/:path`} exact>
          <Product />
        </PrivateRoute>
        <PrivateRoute path={ROUTE_ACCOUNT}>
          <Account />
        </PrivateRoute>
        <PrivateRoute
          layout={{
            Component: ScanbatLayout,
          }}
          path={ROUTE_SCANBAT}
        >
          <Scanbat />
        </PrivateRoute>
        <PrivateRoute path={ROUTE_STANDALONE}>
          <Standalone />
        </PrivateRoute>
        <PrivateRoute path={`${ROUTE_SUPPLIER}/:slug`} noDefaultLayout>
          <Supplier />
        </PrivateRoute>
        {user?.functionality_accesses?.orders && (
          <PrivateRoute
            layout={{
              Component: OrdersLayout,
              props: {},
            }}
            path={ROUTE_ORDERS}
            exact
          >
            <Orders />
          </PrivateRoute>
        )}
        {user?.functionality_accesses?.orders &&
          user?.functionality_accesses?.orders_order && (
            <PrivateRoute
              layout={{
                Component: OrderLayout,
                props: {},
              }}
              path={`${ROUTE_ORDERS}/:id`}
              exact
            >
              <Order />
            </PrivateRoute>
          )}
        {isEbat && user?.functionality_accesses?.quotes && (
          <PrivateRoute
            layout={{
              Component: QuotesLayout,
              props: {},
            }}
            path={ROUTE_QUOTES}
            exact
          >
            <Quotes />
          </PrivateRoute>
        )}
        {isEbat &&
          user?.functionality_accesses?.quotes &&
          user?.functionality_accesses?.quotes_quote && (
            <PrivateRoute
              layout={{
                Component: QuoteLayout,
                props: {},
              }}
              path={[`${ROUTE_QUOTES}/:id`]}
              strict
              exact
            >
              <Quote />
            </PrivateRoute>
          )}
        {isEbat &&
          user?.functionality_accesses?.quotes &&
          user?.functionality_accesses?.quotes_quote && (
            <PrivateRoute
              layout={{
                Component: QuoteLayout,
                props: {},
              }}
              path={[`${ROUTE_QUOTES}/:id${ROUTE_EDIT}`]}
              strict
              exact
            >
              <Quote />
            </PrivateRoute>
          )}
        {isEbat &&
          user?.functionality_accesses?.quotes &&
          user?.functionality_accesses?.quotes_quote && (
            <PrivateRoute
              layout={{
                Component: QuoteLayout,
                props: {},
              }}
              path={[`${ROUTE_QUOTES}/:id${ROUTE_EDIT}${ROUTE_VALIDATE}`]}
              strict
              exact
            >
              <Quote />
            </PrivateRoute>
          )}
        {isEbat && user?.functionality_accesses?.quotes && (
          <PrivateRoute path={`${ROUTE_QUOTES}/:id${ROUTE_CHECKOUT}`} exact>
            <Checkout type={ITEM_TYPES.quote} />
          </PrivateRoute>
        )}
        {isEbat && user?.functionality_accesses?.quotes && (
          <PrivateRoute path={`${ROUTE_QUOTES}${ROUTE_CHECKOUT_CONFIRM}`} exact>
            <CheckoutConfirm type={ITEM_TYPES.quote} />
          </PrivateRoute>
        )}
        {user?.functionality_accesses?.wishlists && (
          <PrivateRoute path={ROUTE_WISHLISTS} exact>
            <Wishlists />
          </PrivateRoute>
        )}
        {user?.functionality_accesses?.wishlists && (
          <PrivateRoute path={`${ROUTE_WISHLISTS}/:id`} exact>
            <Wishlist />
          </PrivateRoute>
        )}
        {user?.functionality_accesses?.carts && (
          <PrivateRoute path={`${ROUTE_CARTS}/:id`} exact>
            <Cart />
          </PrivateRoute>
        )}
        {user?.functionality_accesses?.carts &&
          user?.functionality_accesses?.carts_order_validate && (
            <PrivateRoute path={`${ROUTE_CARTS}/:id${ROUTE_CHECKOUT}`} exact>
              <Checkout type={ITEM_TYPES.cart} />
            </PrivateRoute>
          )}
        {user?.functionality_accesses?.carts &&
          user?.functionality_accesses?.carts_order_validate && (
            <PrivateRoute
              path={`${ROUTE_CARTS}${ROUTE_CHECKOUT_CONFIRM}`}
              exact
            >
              <CheckoutConfirm type={ITEM_TYPES.cart} />
            </PrivateRoute>
          )}
        <PrivateRoute path={ROUTE_CONTACT} exact>
          <Contact />
        </PrivateRoute>
        <PrivateRoute
          layout={{
            Component: EcatalogLayout,
            props: {},
          }}
          noDefaultLayout
          path={`${ROUTE_ECATALOGS}/:id`}
          exact
        >
          <ECatalog />
        </PrivateRoute>
        <PrivateRoute path={ROUTE_ECATALOGS} exact>
          <ECatalogs />
        </PrivateRoute>
        <PrivateRoute path={ROUTE_PARTNERS} exact>
          <Partners />
        </PrivateRoute>
        {/*<PrivateRoute path="/mirage" exact>*/}
        {/*  <Mirage />*/}
        {/*</PrivateRoute>*/}
        <PrivateRoute path={ROUTE_404} exact>
          <Error404 />
        </PrivateRoute>
        <PrivateRoute>
          <Redirect to={ROUTE_404} />
        </PrivateRoute>
      </Switch>
    </Suspense>
  );
};

export default Routes;
